import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import { IApiError } from "common/interface/IApiError";
import { IDropPoint, IDropPointSociety } from "common/interface/IDropPoint";
import { logoutIfNeeded } from "common/utils";

export interface DropPointsState {
  dropPoints: IDropPoint[];
  dropPointsStatus: "idle" | "loading" | "success" | "error";
  dropPointsError?: string;
  societies: IDropPointSociety[];
  societiesStatus: "idle" | "loading" | "success" | "error";
  societiesError?: string;
  mutationStatus: "idle" | "saving" | "success" | "error";
  mutationError?: string;
}

const initialState: DropPointsState = {
  dropPoints: [],
  dropPointsStatus: "idle",
  societies: [],
  societiesStatus: "idle",
  mutationStatus: "idle",
};

export const getDropPoints = createAsyncThunk<IDropPoint[], { storeId: string }>(
  "dropPoints/getDropPoints",
  async ({ storeId }, thunkApi) => {
    try {
      const response = await API.get(apis.dropPoints, {
        headers: { storeid: storeId },
      });
      return response.data as IDropPoint[];
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to fetch drop points";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const getDropPointSocieties = createAsyncThunk<IDropPointSociety[], { dropPointId: number }>(
  "dropPoints/getDropPointSocieties",
  async ({ dropPointId }, thunkApi) => {
    try {
      const response = await API.get(`${apis.dropPoints}/${dropPointId}/societies`);
      return response.data as IDropPointSociety[];
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to fetch societies";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const addDropPointSocieties = createAsyncThunk<
  void,
  { dropPointId: number; societyIds: number[] }
>("dropPoints/addDropPointSocieties", async ({ dropPointId, societyIds }, thunkApi) => {
  try {
    await API.post(`${apis.dropPoints}/${dropPointId}/societies`, { societyIds });
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || "Failed to add societies";
    logoutIfNeeded(error, thunkApi);
    return thunkApi.rejectWithValue({ message: errorMessage });
  }
});

export const removeDropPointSocieties = createAsyncThunk<
  void,
  { dropPointId: number; societyIds: number[] }
>("dropPoints/removeDropPointSocieties", async ({ dropPointId, societyIds }, thunkApi) => {
  try {
    await API.delete(`${apis.dropPoints}/${dropPointId}/societies`, {
      data: { societyIds },
    });
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || "Failed to remove societies";
    logoutIfNeeded(error, thunkApi);
    return thunkApi.rejectWithValue({ message: errorMessage });
  }
});

const dropPointsSlice = createSlice({
  name: "dropPoints",
  initialState,
  reducers: {
    resetDropPointSocieties: (state) => {
      state.societies = [];
      state.societiesStatus = "idle";
      state.societiesError = undefined;
    },
    resetMutationStatus: (state) => {
      state.mutationStatus = "idle";
      state.mutationError = undefined;
    },
  },
  extraReducers(builder) {
    builder
      /**
       * GET DROP POINTS
       */
      .addCase(getDropPoints.pending, (state) => {
        state.dropPointsStatus = "loading";
        state.dropPointsError = undefined;
      })
      .addCase(getDropPoints.fulfilled, (state, action) => {
        state.dropPointsStatus = "success";
        state.dropPoints = action.payload;
      })
      .addCase(getDropPoints.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.dropPointsStatus = "error";
        state.dropPoints = [];
        state.dropPointsError = error?.message || "Failed to fetch drop points";
      })

      /**
       * GET DROP POINT SOCIETIES
       */
      .addCase(getDropPointSocieties.pending, (state) => {
        state.societiesStatus = "loading";
        state.societiesError = undefined;
      })
      .addCase(getDropPointSocieties.fulfilled, (state, action) => {
        state.societiesStatus = "success";
        state.societies = action.payload;
      })
      .addCase(getDropPointSocieties.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.societiesStatus = "error";
        state.societies = [];
        state.societiesError = error?.message || "Failed to fetch societies";
      })

      /**
       * ADD DROP POINT SOCIETIES
       */
      .addCase(addDropPointSocieties.pending, (state) => {
        state.mutationStatus = "saving";
        state.mutationError = undefined;
      })
      .addCase(addDropPointSocieties.fulfilled, (state) => {
        state.mutationStatus = "success";
      })
      .addCase(addDropPointSocieties.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.mutationStatus = "error";
        state.mutationError = error?.message || "Failed to add societies";
      })

      /**
       * REMOVE DROP POINT SOCIETIES
       */
      .addCase(removeDropPointSocieties.pending, (state) => {
        state.mutationStatus = "saving";
        state.mutationError = undefined;
      })
      .addCase(removeDropPointSocieties.fulfilled, (state) => {
        state.mutationStatus = "success";
      })
      .addCase(removeDropPointSocieties.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.mutationStatus = "error";
        state.mutationError = error?.message || "Failed to remove societies";
      });
  },
});

export const { resetDropPointSocieties, resetMutationStatus } = dropPointsSlice.actions;
export default dropPointsSlice.reducer;
